import React, { useState } from "react";
import { createPortalSession } from "../../user-management/services/billingService";
import { useAuth } from "../../../context/AuthContext";
import { useCustomerWorkspace } from "../../../context/CustomerWorkspaceContext";
import { useBillingState } from "../context/BillingStateContext";

/** sessionStorage can throw outright in private browsing, not just return null. */
const readDismissed = (key: string): boolean => {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

/**
 * Shown while a renewal is failing and Stripe is still retrying (SGL-283).
 *
 * Deliberately not alarming. During the retry window the customer keeps full
 * access, because in almost every case this is an expired card or a bank
 * blocking an unfamiliar charge, not somebody refusing to pay. The copy says
 * what happened and offers the one action that fixes it.
 *
 * Dismissable per session, not permanently: a customer who dismisses it and
 * then forgets would have their agents pause with no warning they ever saw.
 */
export const PaymentBanner: React.FC = () => {
  const { idToken } = useAuth();
  const { selectedCustomerId } = useCustomerWorkspace();
  const { showBanner, paymentState, isStaff } = useBillingState();

  // Per Brand. An agency owner who dismissed this on one Brand still needs to
  // be told when a different Brand's card fails.
  const dismissKey = `sagelyn.paymentBannerDismissed.${selectedCustomerId ?? "none"}`;
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const dismissed = dismissedKey === dismissKey || readDismissed(dismissKey);

  if (!showBanner || dismissed) return null;

  const openPortal = async () => {
    if (!idToken) return;
    setLoading(true);
    try {
      const { url } = await createPortalSession(idToken);
      window.location.href = url;
    } catch {
      setLoading(false);
    }
  };

  const dismiss = () => {
    try {
      sessionStorage.setItem(dismissKey, "1");
    } catch {
      /* private browsing — dismissing for this session in memory is still correct */
    }
    setDismissedKey(dismissKey);
  };

  // Staff see this on a paused Brand instead of the paywall, so the wording has
  // to tell them the truth about that Brand rather than address the payer.
  const staffPausedNotice = paymentState === "paused" && isStaff;

  return (
    <div
      role="status"
      className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100"
    >
      <span className="material-icons-outlined text-[18px] leading-none">
        {staffPausedNotice ? "pause_circle" : "credit_card_off"}
      </span>
      <span className="min-w-0 flex-1">
        {staffPausedNotice ? (
          <>
            <strong className="font-semibold">
              This Brand is paused for non-payment.
            </strong>{" "}
            You can still see and change everything. The customer cannot, and
            their agents will not publish.
          </>
        ) : (
          <>
            <strong className="font-semibold">
              Your last payment didn&apos;t go through.
            </strong>{" "}
            Everything is still running. Update your card to keep it that way.
          </>
        )}
      </span>
      {!staffPausedNotice && (
        <button
          type="button"
          onClick={openPortal}
          disabled={loading || !idToken}
          className="shrink-0 rounded-md bg-amber-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-900 disabled:opacity-60"
        >
          {loading ? "Opening…" : "Update payment method"}
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded p-1 text-amber-700 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/40"
      >
        <span className="material-icons-outlined text-[18px] leading-none">close</span>
      </button>
    </div>
  );
};

export default PaymentBanner;
